import { MyRouterContext } from '@/App';
import { MainContainer } from '@/components/MainContainer';
import { PageHeader } from '@/components/PageHeader';
import { logger } from '@/lib/logger';
import { notifyError, notifySuccess } from '@/lib/notification';
import { Box, Text, Title } from '@mantine/core';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, LoaderFnContext, useRouter } from '@tanstack/react-router';
import { CreditList } from './-components/CreditList';
import { Inputs, UpdateTenantForm } from './-components/UpdateTenantForm';
import { DowngradeButton } from './-components/DowngradeButton';
import { DowngradeScheduled } from './-components/DowngradeScheduled';

export const Route = createFileRoute('/_main/tenant/settings/')({
  loader,
  component: Component,
});

async function loader({
  context: { adminClient },
}: LoaderFnContext<Record<never, string>, never, MyRouterContext>) {
  const tenant = await adminClient.settings_v1_get_tenant();
  const credits = await adminClient.admin_v1_get_tenant_credits();
  return {
    tenant,
    adminClient,
    credits: credits.items,
  };
}

function Component() {
  const { tenant, adminClient, credits } = Route.useLoaderData();
  const router = useRouter();
  const client = useQueryClient();

  const updateTenantName = useMutation({
    mutationFn: ({ name, enableMfa, encryptReasoning }: Inputs) =>
      adminClient.admin_v1_update_tenant({
        name,
        enable_mfa: enableMfa,
        ...(encryptReasoning !== undefined ? { encrypt_reasoning: encryptReasoning } : {}),
      }),
    onSuccess: async () => {
      client.invalidateQueries({ queryKey: ['tenant'] });
      await router.load();
      notifySuccess('テナント設定を更新しました。');
    },
    onError: (error) => {
      logger.error(error);
      notifyError('テナント設定の更新に失敗しました。');
    },
  });

  const downgrade = useMutation({
    mutationFn: () => adminClient.settings_v1_downgrade_tenant_to_free(undefined),
    onSuccess: async () => {
      await router.load();
      notifySuccess('無料プランへの変更を受け付けました。');
    },
    onError: (error) => {
      logger.error(error);
      notifyError('無料プランへの変更に失敗しました。');
    },
  });

  const cancelDowngrade = useMutation({
    mutationFn: () => adminClient.settings_v1_cancel_tenant_downgrade(undefined),
    onSuccess: async () => {
      await router.load();
      notifySuccess('無料プランへの変更を取り消しました。');
    },
    onError: (error) => {
      logger.error(error);
      notifyError('無料プランへの変更の取り消しに失敗しました。');
    },
  });

  return (
    <MainContainer>
      <PageHeader title="テナント設定" />
      <UpdateTenantForm
        tenant={tenant}
        tenantMetadata={tenant.tenant_metadata}
        onSubmit={(data) => updateTenantName.mutate(data)}
      />
      {credits.length > 0 && (
        <>
          <Title order={4} mt="100px" mb="lg">
            付与されたクレジット
          </Title>
          <Text size="sm" my="lg">
            未使用のクレジットが残っている場合、請求時に有効期限が短いものから優先して使用されます。
          </Text>
          <Box maw="600px">
            <CreditList credits={credits}></CreditList>
          </Box>
          <Text size="xs" my="lg">
            ※ 支払いに使用されたクレジットの金額は、請求書をご確認ください。
          </Text>
        </>
      )}
      <Title order={4} mt="100px" mb="lg">
        プラン
      </Title>
      {/* downgrade_at が無い要求は Stripe の予約に失敗している。ボタンを出して再実行させる */}
      {tenant.spec.downgrade_requested && tenant.spec.downgrade_at ? (
        <DowngradeScheduled
          downgradeAt={tenant.spec.downgrade_at}
          onCancel={() => cancelDowngrade.mutate()}
          loading={cancelDowngrade.isPending}
        />
      ) : (
        <DowngradeButton onConfirm={() => downgrade.mutate()} loading={downgrade.isPending} />
      )}
    </MainContainer>
  );
}
